import * as angular from "angular";
import {IController} from "angular";
import * as _ from "lodash";
import {
  buildDefaultDayTimeTemplate,
  PLANNING_DRAFT_GLOBAL_TAB_ID,
  PlanningDraftLayer
} from "../../models/planning-draft-layer.model";

export class PlanningProjectSettingsSectionNewComponent implements IController {

  project?: any;
  draft: any;
  draftTemplate: any;
  template: any;
  isTemplateMode: boolean;
  isProjectMode: boolean;
  depots: any[];
  selectedJobs: any[];
  onChange: Function;
  onChangeDraftTemplate: Function;
  onChangeTemplate: Function;
  onChangeProject: Function;
  onPendingClusteringChange: Function;


  public draftSettingsCopy: any;
  public globalSettings: any = {};
  public globalTabId: string = PLANNING_DRAFT_GLOBAL_TAB_ID;
  public layers: PlanningDraftLayer[] = [];
  public activeLayerId: string;
  public editingLayerId: string;
  public draggedLayerId: string;
  public dragOverLayerId: string;
  public isSettingsFormDisabled: boolean = false;

  /* @ngInject */
  constructor(private UUIDService) {
  }

  $onInit() {
    this._initLayers();
    this.draftSettingsCopy = angular.copy(this.globalSettings);

    if (this.draft && !this.draft.clusteringAreaIds) {
      this.draft.clusteringAreaIds = [];
    }

    if (this.template && !this.template.clusteringAreaIds) {
      this.template.clusteringAreaIds = [];
    }

    if (this.isProjectMode && this.project && !this.project.clusteringAreaIds) {
      this.project.clusteringAreaIds = [];
    }

    this._syncFromLayers();
    this._updateSettingsFormDisabled();
  }

  onDraftNameChange() {
    this._syncFromLayers();
  }

  onTemplateFieldsChange(template: any) {
    if (this.isTemplateMode) {
      this.template = template;
      this.onGlobalSettingsChange(this.template.settings);
      this._syncFromLayers();
    }
  }

  onClusteringAreasChange(clusteringAreas) {
    if (_.isFunction(this.onPendingClusteringChange)) {
      this.onPendingClusteringChange({clusteringAreas: clusteringAreas});
    }
  }

  onDraftChange(draft) {
    if (!this.draft || !draft) {
      return;
    }

    this.draft.clusteringAreaIds = draft.clusteringAreaIds;
    this._syncFromLayers();
  }

  isGlobalTabSelected(): boolean {
    return this.activeLayerId === this.globalTabId;
  }

  // ******************************
  //        Layer tab actions
  // ******************************

  onSelectLayer(layerId: string) {
    if (this.editingLayerId && this.editingLayerId !== layerId) {
      this.editingLayerId = undefined;
    }
    this.activeLayerId = layerId;
    this._syncFromLayers();
  }

  onAddLayer() {
    const sourceLayer = this._getPreviousLayerSource();
    const layer = this._cloneLayerFromSource(sourceLayer, this.UUIDService.generate());
    this.layers.push(layer);
    this.activeLayerId = layer.id;
    this._syncFromLayers();
  }

  onRemoveLayer(layerId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    const index = _.findIndex(this.layers, {id: layerId});
    if (index === -1) {
      return;
    }
    this.layers.splice(index, 1);
    if (this.activeLayerId === layerId) {
      const nextIndex = Math.max(0, index - 1);
      this.activeLayerId = this.layers[nextIndex]?.id || this.globalTabId;
    }
    if (this.editingLayerId === layerId) {
      this.editingLayerId = undefined;
    }
    this._syncFromLayers();
  }

  onStartRenameLayer(layerId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.editingLayerId = layerId;
    this.activeLayerId = layerId;
  }

  onCommitRenameLayer(layer: PlanningDraftLayer, name: string) {
    const trimmed = (name || '').trim();
    layer.name = trimmed || this._getNextLayerName(this.layers.indexOf(layer));
    this.editingLayerId = undefined;
    this._syncFromLayers();
  }

  onToggleLayerDisabled(layer: PlanningDraftLayer, disabled: boolean) {
    layer.disabled = disabled;
    this._syncFromLayers();
  }

  // ******************************
  //        Drag and drop
  // ******************************

  onLayerDragStart(layerId: string, event: DragEvent) {
    this.draggedLayerId = layerId;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', layerId);
    }
  }

  onLayerDragOver(layerId: string, event: DragEvent) {
    if (!this.draggedLayerId || this.draggedLayerId === layerId) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this.dragOverLayerId = layerId;
  }

  onLayerDragLeave(layerId: string) {
    if (this.dragOverLayerId === layerId) {
      this.dragOverLayerId = undefined;
    }
  }

  onLayerDrop(targetLayerId: string, event: DragEvent) {
    event.preventDefault();
    const sourceId = this.draggedLayerId;
    this.draggedLayerId = undefined;
    this.dragOverLayerId = undefined;
    if (!sourceId || sourceId === targetLayerId) {
      return;
    }
    const fromIndex = _.findIndex(this.layers, {id: sourceId});
    const toIndex = _.findIndex(this.layers, {id: targetLayerId});
    if (fromIndex === -1 || toIndex === -1) {
      return;
    }
    const [moved] = this.layers.splice(fromIndex, 1);
    this.layers.splice(toIndex, 0, moved);
    this._syncFromLayers();
  }

  onLayerDragEnd() {
    this.draggedLayerId = undefined;
    this.dragOverLayerId = undefined;
  }

  // ******************************
  //        Settings
  // ******************************

  onGlobalSettingsChange(settings: any) {
    const settingsCopy = angular.copy(settings);
    _.unset(settingsCopy, 'optimizationSteps');
    this.globalSettings = settingsCopy;
    this.draftSettingsCopy = angular.copy(settingsCopy);
    this._syncFromLayers();
  }

  onActiveLayerSettingsChange(settings: any) {
    const activeLayer = this._getActiveLayer();
    if (!activeLayer) {
      return;
    }
    activeLayer.settings = angular.copy(settings);
    _.unset(activeLayer.settings, 'optimizationSteps');
    this._syncFromLayers();
  }

  onActiveLayerStepChange() {
    this._syncFromLayers();
  }

  // ******************************
  //        Default draft template
  // ******************************

  onChangeTemplateDefaultDraft(id, draftTemplate) {
    if (this.isProjectMode) {
      const projectCopy = angular.copy(this.project);

      if (!draftTemplate) {
        projectCopy.useDraftTemplateSettings = false;
        projectCopy.draftTemplateId = undefined;
        if (this.isGlobalTabSelected()) {
          this.globalSettings = angular.copy(this.draftSettingsCopy);
        } else {
          const activeLayer = this._getActiveLayer();
          if (activeLayer) {
            activeLayer.settings = angular.copy(this.draftSettingsCopy);
          }
        }
      } else {
        projectCopy.draftTemplateId = draftTemplate.id;
        projectCopy.useDraftTemplateSettings = !!draftTemplate.settings;
        if (projectCopy.useDraftTemplateSettings) {
          if (this.isGlobalTabSelected()) {
            this.globalSettings = angular.copy(draftTemplate.settings);
          } else {
            const activeLayer = this._getActiveLayer();
            if (activeLayer) {
              activeLayer.settings = angular.copy(draftTemplate.settings);
            }
          }
        }
      }

      this._applySettingsPayload(projectCopy);
      this.onChangeDraftTemplate({project: projectCopy, draftTemplate: draftTemplate});
      this._updateSettingsFormDisabled(projectCopy);
      return;
    }

    const draftCopy = angular.copy(this.draft);

    if (!draftTemplate) {
      draftCopy.useDraftTemplateSettings = false;
      draftCopy.draftTemplateId = undefined;
      if (this.isGlobalTabSelected()) {
        this.globalSettings = angular.copy(this.draftSettingsCopy);
      } else {
        const activeLayer = this._getActiveLayer();
        if (activeLayer) {
          activeLayer.settings = angular.copy(this.draftSettingsCopy);
        }
      }
    } else {
      draftCopy.draftTemplateId = draftTemplate.id;
      draftCopy.useDraftTemplateSettings = !!draftTemplate.settings;
      if (draftCopy.useDraftTemplateSettings) {
        if (this.isGlobalTabSelected()) {
          this.globalSettings = angular.copy(draftTemplate.settings);
        } else {
          const activeLayer = this._getActiveLayer();
          if (activeLayer) {
            activeLayer.settings = angular.copy(draftTemplate.settings);
          }
        }
      }
    }

    this._applySettingsPayload(draftCopy);
    this.onChangeDraftTemplate({draft: draftCopy, draftTemplate: draftTemplate});
    this._updateSettingsFormDisabled(draftCopy);
  }

  // ******************************
  //        Private
  // ******************************

  private _initLayers() {
    this.globalSettings = {};
    this.layers = [];

    const source = this.isTemplateMode ? this.template : (this.isProjectMode ? this.project : this.draft);

    if (_.get(source, 'settings')) {
      this.globalSettings = angular.copy(source.settings);
      _.unset(this.globalSettings, 'optimizationSteps');
    }

    if (_.get(source, 'optimizationSteps.length')) {
      this.layers = source.optimizationSteps.map((step, index) => this._mapStepToLayer(step, index));
    } else if (_.get(source, 'settings.optimizationSteps.length')) {
      this.layers = source.settings.optimizationSteps.map((step, index) => this._mapStepToLayer(step, index));
    }

    this._migrateEmptyLayerSettings();
    this.activeLayerId = this.globalTabId;
  }

  private _migrateEmptyLayerSettings() {
    if (_.isEmpty(this.globalSettings) || !this.layers.length) {
      return;
    }
    this.layers.forEach((layer) => {
      if (_.isEmpty(layer.settings)) {
        layer.settings = angular.copy(this.globalSettings);
      }
    });
  }

  private _resolveLayerSettings(layer: PlanningDraftLayer): any {
    let settings = angular.copy(layer.settings || {});
    if (_.isEmpty(settings) && !_.isEmpty(this.globalSettings)) {
      settings = angular.copy(this.globalSettings);
    }
    _.unset(settings, 'optimizationSteps');
    return settings;
  }

  private _mapStepToLayer(step: any, index: number): PlanningDraftLayer {
    const settings = angular.copy(step.settings || {});
    _.unset(settings, 'optimizationSteps');

    const layer: PlanningDraftLayer = {
      id: this.UUIDService.generate(),
      stepOrder: step.stepOrder || index + 1,
      name: step.name || this._getLayerNameByIndex(index),
      jobFilter: angular.copy(step.jobFilter),
      resourceFilter: angular.copy(step.resourceFilter),
      settings: settings,
      disabled: !!step.disabled
    };

    if (this.isTemplateMode) {
      layer.jobFilterDayTimeTemplate = step.jobFilter
        ? (angular.copy(step.jobFilterDayTimeTemplate) || buildDefaultDayTimeTemplate())
        : undefined;
      layer.resourceFilterDayTimeTemplate = step.resourceFilter
        ? (angular.copy(step.resourceFilterDayTimeTemplate) || buildDefaultDayTimeTemplate())
        : undefined;
    }

    return layer;
  }

  private _getActiveLayer(): PlanningDraftLayer | undefined {
    return _.find(this.layers, {id: this.activeLayerId});
  }

  private _getLayerNameByIndex(index: number): string {
    return `Layer ${index + 1}`;
  }

  private _getNextLayerName(skipIndex?: number): string {
    const usedNames = this.layers
      .filter((_layer, i) => i !== skipIndex)
      .map(l => l.name);
    let n = this.layers.length + 1;
    while (_.includes(usedNames, `Layer ${n}`)) {
      n++;
    }
    return `Layer ${n}`;
  }

  private _getPreviousLayerSource(): PlanningDraftLayer | null {
    if (!this.layers.length) {
      return null;
    }
    return this.layers[this.layers.length - 1];
  }

  private _cloneLayerFromSource(sourceLayer: PlanningDraftLayer | null, id: string): PlanningDraftLayer {
    const layer: PlanningDraftLayer = {
      id: id,
      stepOrder: this.layers.length + 1,
      name: this._getNextLayerName(),
      settings: {},
      disabled: false
    };

    if (!sourceLayer) {
      layer.settings = angular.copy(this.globalSettings || {});
      _.unset(layer.settings, 'optimizationSteps');
      return layer;
    }

    layer.settings = angular.copy(sourceLayer.settings || {});
    _.unset(layer.settings, 'optimizationSteps');

    if (sourceLayer.jobFilter) {
      layer.jobFilter = this._sanitizeFilter(sourceLayer.jobFilter);
      if (this.isTemplateMode) {
        layer.jobFilterDayTimeTemplate = angular.copy(sourceLayer.jobFilterDayTimeTemplate) || buildDefaultDayTimeTemplate();
      }
    }
    if (sourceLayer.resourceFilter) {
      layer.resourceFilter = this._sanitizeFilter(sourceLayer.resourceFilter);
      if (this.isTemplateMode) {
        layer.resourceFilterDayTimeTemplate = angular.copy(sourceLayer.resourceFilterDayTimeTemplate) || buildDefaultDayTimeTemplate();
      }
    }

    return layer;
  }

  private _sanitizeFilter(filter: any): any {
    if (!filter) {
      return filter;
    }
    const copy = angular.copy(filter);
    _.forEach(_.keys(copy), (key) => {
      if (key.charAt(0) === '_') {
        delete copy[key];
      }
    });
    return copy;
  }

  private _buildOptimizationSteps(): any[] {
    return this.layers.map((layer, index) => {
      const resolvedSettings = this._resolveLayerSettings(layer);
      const step: any = {
        stepOrder: index + 1,
        name: layer.name,
        settings: resolvedSettings,
        disabled: !!layer.disabled
      };

      if (layer.jobFilter) {
        step.jobFilter = this._sanitizeFilter(layer.jobFilter);
        if (this.isTemplateMode && layer.jobFilterDayTimeTemplate) {
          step.jobFilterDayTimeTemplate = angular.copy(layer.jobFilterDayTimeTemplate);
        }
      }
      if (layer.resourceFilter) {
        step.resourceFilter = this._sanitizeFilter(layer.resourceFilter);
        if (this.isTemplateMode && layer.resourceFilterDayTimeTemplate) {
          step.resourceFilterDayTimeTemplate = angular.copy(layer.resourceFilterDayTimeTemplate);
        }
      }

      return step;
    });
  }

  private _applySettingsPayload(ownerCopy: any) {
    const settings = angular.copy(this.globalSettings || {});
    _.unset(settings, 'optimizationSteps');
    ownerCopy.settings = settings;
    ownerCopy.optimizationSteps = this._buildOptimizationSteps();
  }

  private _updateSettingsFormDisabled(owner?: any) {
    const source = owner || (this.isProjectMode ? this.project : this.draft);
    this.isSettingsFormDisabled = !!source?.draftTemplateId && !!source?.useDraftTemplateSettings;
  }

  private _syncFromLayers() {
    if (this.isTemplateMode) {
      const templateCopy = angular.copy(this.template);
      this._applySettingsPayload(templateCopy);
      if (this.isGlobalTabSelected()) {
        this.draftSettingsCopy = angular.copy(this.globalSettings);
      }
      if (_.isFunction(this.onChangeTemplate)) {
        this.onChangeTemplate({template: templateCopy});
      }
      this._updateSettingsFormDisabled();
      return;
    }

    if (this.isProjectMode) {
      const projectCopy = angular.copy(this.project);
      this._applySettingsPayload(projectCopy);
      if (this.isGlobalTabSelected()) {
        this.draftSettingsCopy = angular.copy(this.globalSettings);
      }
      if (_.isFunction(this.onChangeProject)) {
        this.onChangeProject({project: projectCopy});
      }
      this._updateSettingsFormDisabled();
      return;
    }

    const draftCopy = angular.copy(this.draft);
    this._applySettingsPayload(draftCopy);
    if (this.isGlobalTabSelected()) {
      this.draftSettingsCopy = angular.copy(this.globalSettings);
    }
    this.onChange({draft: draftCopy});

    this._updateSettingsFormDisabled();
  }


}

angular
  .module('bringoz.admin.planning')
  .component('planningProjectSettingsSectionNew', {
    bindings: {
      project: '<?',
      draft: '<?',
      draftTemplate: '<?',
      template: '<?',
      isTemplateMode: '<?',
      isProjectMode: '<?',
      depots: '<?',
      selectedJobs: '<?',
      onChange: '&?',
      onChangeDraftTemplate: '&?',
      onChangeTemplate: '&?',
      onChangeProject: '&?',
      onPendingClusteringChange: '&?'
    },
    template: require('./planning-project-settings-section-new.html').default,
    controller: PlanningProjectSettingsSectionNewComponent
  });
